import cx from 'clsx';
import { convert } from 'html-to-text';
import { marked } from 'marked';
import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as CloseIcon } from '@assets/svg/ic-close.svg';
import { ReactComponent as CreateNewChatIcon } from '@assets/svg/ic-createChat.svg';

import TextArea from '@components/form/TextArea/TextArea';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppRtl } from '@store/useAppStore';

import type { IChatMessage } from '../../model/chatMessage';
import { useHelperAiMessageMutation } from '../../services/useHelperAiMessageMutation';
import { selectChatId, selectChatIsOpen, selectChatMessages, useChatActions } from '../../store/useChatStore';
import ChatCharacter from '../ChatCharacter/ChatCharacter';
import ChatMessage from '../ChatMessage/ChatMessage';
import ConfirmNewChatModal from '../ConfirmNewChatModal/ConfirmNewChatModal';
import SendBtnWithLoader from '../SendBtnWithLoader/SendBtnWithLoader';
import style from './style.module.scss';

const MESSAGE_TOOLTIP_ID = 'send-again-btn';

const ChatWindow = () => {
  const { t } = useTranslation();
  const rtl = selectAppRtl();

  const chatIsOpen = selectChatIsOpen();

  const { data: profileData } = useUserProfileQuery();
  const dateTimeFormat = `${profileData?.dateFormat.date}, ${profileData?.dateFormat.time}`;

  const [newMessage, setNewMessage] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const chatRef = useRef<HTMLDivElement | null>(null);

  const { mutate: helperMessageMutate, isLoading: helperMessageLoading } = useHelperAiMessageMutation();

  const { setChatMessages, setChatId, setChatIsOpen, setMessageHasError, setChatClear } = useChatActions();

  const chatMessages: Array<IChatMessage> = selectChatMessages();
  const chatId = selectChatId();
  const lastMessage = chatMessages[chatMessages.length - 1];
  const isDisabled = newMessage.length <= 0 || lastMessage.hasError;

  const scrollToBottom = (time?: number, messageId?: number) => {
    const chatBody = chatRef.current;

    if (chatBody) {
      setTimeout(() => {
        if (messageId) {
          const messageItem = document.getElementById(`${messageId}`);

          messageItem?.scrollIntoView({ behavior: 'smooth' });
        } else {
          chatBody.scrollTop = chatBody.scrollHeight;
        }
      }, time || 0);
    }
  };

  const sendMessage = (content?: string) => {
    const messageContent = newMessage || content;
    if (messageContent) {
      if (!content) {
        setChatMessages({ content: newMessage, role: 'user', createdAt: new Date() });
        setNewMessage('');
        scrollToBottom(100);
      } else {
        setMessageHasError(false);
      }

      helperMessageMutate(
        { content: messageContent, chatId: chatId },
        {
          onSuccess: ({ data }) => {
            if (data?.data) {
              const newMessageItem = data.data;
              setChatMessages({ ...newMessageItem, content: marked.parse(newMessageItem.content) });
              setChatId(newMessageItem.chatId);
              scrollToBottom(100, newMessageItem?.id);
            }
          },
          onError: (error) => {
            if (
              error.response?.status === 422 &&
              (error.response?.data?.message === 'Session expired.' ||
                error.response?.data?.message === 'Chat not found.')
            ) {
              setIsExpired(true);

              return;
            }

            setMessageHasError(true);
          },
        },
      );
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!helperMessageLoading && e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCreateNewSession = () => {
    setIsExpired(false);
    setChatClear();
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setChatClear();
  };

  const handleCopyMessage = async (message: string) => {
    const messageContent = convert(message, {
      wordwrap: null,
      selectors: [{ selector: 'ul', options: { itemPrefix: ' - ' } }],
    });
    const blob = new Blob([messageContent], { type: 'text/plain' });
    const data = [new ClipboardItem({ ['text/plain']: blob })];

    await navigator.clipboard.write(data);
    showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
  };

  useEffect(() => {
    if (chatIsOpen) {
      scrollToBottom();
    }
  }, [chatIsOpen]);

  if (!chatIsOpen) {
    return null;
  }

  return (
    <div className={cx(style.chatWrapper, { [style.confirmModalWrapper]: showConfirmModal })}>
      <div className={style.chatHead}>
        <div className={style.chatTitle}>{t('AI Assistant')}</div>
        <div className={style.chatHeadActions}>
          <CreateNewChatIcon className={style.createChatIcon} onClick={() => setShowConfirmModal(true)} />
          <CloseIcon className={cx(style.closeIcon, { [style.rtl]: rtl })} onClick={setChatIsOpen} />
        </div>
      </div>
      <div ref={chatRef} className={style.chatBody}>
        {chatMessages.map((message) => (
          <ChatMessage
            key={uuidv4()}
            messageItem={message}
            userImage={profileData?.user?.userpic}
            dateTimeFormat={dateTimeFormat}
            tooltipId={MESSAGE_TOOLTIP_ID}
            sendMessage={sendMessage}
            handleCopyMessage={handleCopyMessage}
          />
        ))}
        {isExpired && (
          <div className={style.expiredMessage}>
            {t('This chat is inactive.')}{' '}
            <span className={style.newSessionBtn} onClick={handleCreateNewSession}>
              {t('Click here')}
            </span>{' '}
            {t('to start a new chat to continue. Copy any important info before you leave.')}
          </div>
        )}
        {showConfirmModal && (
          <ConfirmNewChatModal
            onConfirm={handleCloseConfirmModal}
            onCancel={() => setShowConfirmModal(false)}
          />
        )}
      </div>
      <div className={style.chatFooter}>
        {!isExpired && (
          <div className={style.fieldWrapper}>
            <TextArea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              rows={1}
              type={'woHelperText'}
              autoHeight
              rightElement={
                <div className={style.commentSendBlock}>
                  <SendBtnWithLoader
                    isActive={!!newMessage}
                    isLoading={helperMessageLoading}
                    isDisabled={!!isDisabled}
                    handleSend={() => sendMessage()}
                  />
                </div>
              }
              onKeyDown={handleInputKeyDown}
            />
          </div>
        )}
        <ChatCharacter isLoading={helperMessageLoading} hasError={lastMessage.hasError} />
      </div>
      <Tooltip id={MESSAGE_TOOLTIP_ID} />
    </div>
  );
};

export default ChatWindow;
